import React from 'react';
import { Box, Stack, Typography, Tooltip, StackProps, Button } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import CustomDropdown from 'components/Dropdown';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';
import { ReactComponent as RedirectIcon } from '../../../assets/PepWise2.0/Redirect.svg';

type KpiCardProps = {
  isLoading?: boolean;
  title: string | React.ReactNode;
  number: number | string;
  isCost?: boolean;
  isPercentage?: boolean;
  suffixFontSize?: number | string;
  change?: number;
  changeType?: 'increase' | 'decrease';
  comparePeriod?: "year" | "LAST_30_DAYS" | "LAST_60_DAYS" | "LAST_90_DAYS" | "LAST_120_DAYS";
  showChangePercentage?: boolean;
  showPeriodChange?: boolean;
  reverseTrendColor?: boolean;
  color?: string;
  numberColor?: string;
  units?: null | string | string[];
  handleEvent?: () => void;
  hasDropdown?: boolean;
  dropdownValue?: string;
  dropdownOptions?: { label: string; value: string }[];
  onDropdownChange?: (value: string) => void;
  containerProps?: StackProps;
  tooltipLabel?: string;
  hasTooltip?: boolean;
  isTextTooltip?: boolean;
  hoverText?: string;
  isSelected?: boolean;
  hasModal?: boolean;
  modalText?: string;
  onModalClick?: () => void;
  theme?: 'dark' | 'light';
  isCustomLabel?: boolean;
  customLabelText?: string;
  customLabelProps?: any;
  numbersx?: any;
};

// ─── Theme tokens ─────────────────────────────────────────────────────────────

const themeTokens = {
  dark: {
    cardBg: 'primary.main',
    hoverBg: '#004080',
    titleColor: '#ffffff',
    numberColor: '#ffffff',
    periodChangeColor: '#A9ADCB',
    infoIconColor: '#ffffff',
    dropdownSelectColor: '#ffffff',
    modalButtonColor: '#ffffff',
    modalButtonHoverBg: '#004794ff',
    // Info tooltip
    tooltipBg: undefined as string | undefined,
    tooltipBorder: undefined as string | undefined,
    tooltipTextColor: undefined as string | undefined,
    tooltipArrowColor: undefined as string | undefined,
    // Hover number tooltip
    hoverTooltipBg: '#003771 !important',
    hoverTooltipBorder: '1px solid #A9ADCB',
    hoverTooltipTextColor: '#A9ADCB',
    hoverTooltipArrowColor: '#003771',
  },
  light: {
    cardBg: '#ffffff',
    hoverBg: '#f0f4f8',
    titleColor: '#1a1a2e',
    numberColor: '#1a1a2e',
    periodChangeColor: '#667085',
    infoIconColor: '#009BD8',
    dropdownSelectColor: '#1a1a2e',
    modalButtonColor: '#1a1a2e',
    modalButtonHoverBg: '#e8f4fb',
    // Info tooltip
    tooltipBg: '#ffffff !important',
    tooltipBorder: '1px solid #d0d5dd',
    tooltipTextColor: '#000000',
    tooltipArrowColor: '#ffffff',
    // Hover number tooltip
    hoverTooltipBg: '#ffffff !important',
    hoverTooltipBorder: '1px solid #d0d5dd',
    hoverTooltipTextColor: '#000000',
    hoverTooltipArrowColor: '#ffffff',
  },
};

// ─────────────────────────────────────────────────────────────────────────────

const KpiCard = ({
  isLoading = false,
  title,
  number,
  change,
  changeType = 'increase',
  comparePeriod = 'year',
  showChangePercentage = false,
  showPeriodChange = true,
  color,         // no default — resolved from theme tokens below
  numberColor,   // no default — resolved from theme tokens below
  units = null,
  isCost = false,
  isPercentage = false,
  suffixFontSize = 20,
  reverseTrendColor = true,
  handleEvent,
  hasDropdown = false,
  dropdownValue,
  dropdownOptions = [],
  onDropdownChange,
  containerProps,
  tooltipLabel,
  hasTooltip = false,
  isTextTooltip = false,
  hoverText,
  isSelected = false,
  hasModal = false,
  modalText = 'View',
  onModalClick,
  theme = 'dark',
  isCustomLabel = false,
  customLabelText,
  customLabelProps,
  numbersx
}: KpiCardProps) => {

  const t = themeTokens[theme];
  const muiTheme = useTheme();
  const colors = muiTheme.palette;
  const isLightTheme = theme === 'light';

  // Caller-supplied colors take precedence; fall back to theme tokens
  const resolvedTitleColor  = color       ?? (isLightTheme ? colors.widget?.text?.title : t.titleColor);
  const resolvedNumberColor = numberColor ?? (isLightTheme ? colors.widget?.text?.value : t.numberColor);

  const isClickable = !!handleEvent || !!containerProps?.onClick;
  const cursorStyle = isClickable ? 'pointer' : 'default';

const getChangeColor = () => {
  if (change === undefined || change === 0) return colors.widget?.text?.noChange;
  if (reverseTrendColor) return changeType === 'increase' ? colors.widget?.text?.changeUp : colors.widget?.text?.changeDown;
  return changeType === 'increase' ? colors.widget?.text?.changeDown : colors.widget?.text?.changeUp;
};

  const periodLabelMap: Record<string, string> = {
    year: 'last year',
    LAST_30_DAYS: 'last month',
    LAST_60_DAYS: 'last 60 days',
    LAST_90_DAYS: 'last 90 days',
    LAST_120_DAYS: 'last 120 days',
  };

  const formatNumber = (value: number | string): string | number => {
    if (typeof value !== 'number') return value;
    if (Number.isInteger(value)) return value;
    return value.toFixed(isPercentage ? 1 : 2);
  };

  const getPercentageText = () => {
    if (change === 0) return '0%';
    const periodText = showPeriodChange ? 'vs ' + periodLabelMap[comparePeriod] : '';
    return `${change}% ${periodText}`;
  };

  const displayNumberWithUnits = ({
    value,
    unit,
    color: numColor,
    isCost: cost,
  }: {
    value: number | string;
    unit?: string;
    color?: string;
    isCost?: boolean;
  }) => (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography
        variant="metricValue"
        sx={{
          fontWeight: theme === 'light' ? 400 : 600,
          color: numColor,
          display: 'flex',
          alignItems: 'baseline',
          gap: 1,
          ...numbersx
        }}

      >
        {cost && <Box component="span">$</Box>}
        <Box component="span">{formatNumber(value)}</Box>
        {isPercentage && <Box component="span">%</Box>}
        {unit && (
          <Box
            component="span"
            sx={{ fontSize: suffixFontSize, fontWeight: theme === 'light' ? 400 : 600, ml: 1 }}
          >
            {unit}
          </Box>
        )}
      </Typography>
    </Stack>
  );



  // ── Number content (with optional hover tooltip) ───────────────────────────
  const renderNumberContent = () => {
    if (isLoading) return <LoaderComponent />;

    const content = displayNumberWithUnits({
      value: number,
      unit: Array.isArray(units) ? units[0] : units ?? undefined,
      color: resolvedNumberColor,
      isCost,
    });

    if (hoverText) {
      return (
        <Tooltip
          title={
            <Box sx={{ color: isLightTheme ? colors.tooltip?.text?.primary : t.hoverTooltipTextColor, fontSize: '12px' }}>
              {hoverText}
            </Box>
          }
          arrow
          followCursor
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: isLightTheme ? `${colors.tooltip?.background?.primary} !important` : t.hoverTooltipBg,
                border: isLightTheme ? `1px solid ${colors.DropdownV2?.borderColor}` : t.hoverTooltipBorder,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                '& .MuiTooltip-arrow': { color: isLightTheme ? colors.tooltip?.background?.primary : t.hoverTooltipArrowColor },
              },
            },
          }}
        >
          <Box sx={{ cursor: cursorStyle }}>{content}</Box>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <Box
      display="flex"
      flexGrow={1}
      height="100%"
      onClick={handleEvent}
      sx={{ cursor: cursorStyle }}
      {...containerProps}
    >
      <Stack
        minHeight="140px"
        p={2}
        borderRadius="5px"
        flexGrow={1}
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          cursor: cursorStyle,
          transition: 'all 0.25s ease-in-out',
          bgcolor: isLightTheme ? colors.widget?.background?.card : t.cardBg,
          // ── identical border-bottom behaviour across both themes ──
          borderBottom: isSelected ? '4px solid #009BD8' : '4px solid transparent',
          ...(isClickable && {
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-2px)',
              backgroundColor: isLightTheme ? colors.widget?.background?.card : t.hoverBg,
            },
          }),
          ...containerProps?.sx,
        }}
      >
        {/* Title Row */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Typography
            variant="widgetTitle"
            sx={{
              color: resolvedTitleColor,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {typeof title === 'string' ? <HighlightedText text={title} /> : title}
            {isTextTooltip && hasTooltip && (isLightTheme ? <InfoTooltip label={tooltipLabel || ''} /> : renderTooltipIcon(tooltipLabel || '', theme))}
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            {!isTextTooltip && hasTooltip && (isLightTheme ? <InfoTooltip label={tooltipLabel || ''} /> : renderTooltipIcon(tooltipLabel || '', theme))}

            {hasDropdown && dropdownValue && dropdownOptions.length > 0 && (
              <CustomDropdown
                value={dropdownValue}
                options={dropdownOptions}
                handleChange={(e: any) => onDropdownChange?.(e.target.value)}
                {...(isLightTheme && {
                  dropdownIcon: KeyboardArrowDownIcon,
                  selectedTextColor: colors.DropdownV2?.dropdownIconDefault,
                  textColor: colors.DropdownV2?.menuItemText,
                  iconColor: colors.DropdownV2?.dropdownIconDefault,
                  bgColor: colors.DropdownV2?.menuItemBackground,
                  borderColor: 'transparent',
                  hoverBgColor: colors.DropdownV2?.menuItemHover,
                  hoverTextColor: colors.DropdownV2?.menuItemHoverText,
                  selectedBgColor: colors.DropdownV2?.menuItemSelectedBg,
                })}
                boxProps={{
                  sx: {
                      backgroundColor: 'transparent !important',
                      width: 'fit-content',
                      minWidth: 'unset',
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent !important',
                          width: 'fit-content',
                          minWidth: 'unset'
                      },
                      '& .MuiSelect-select': {
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: 500,
                          padding: '0 24px 0 8px !important',
                          minHeight: '28px !important',
                          width: 'auto',
                          minWidth: 'unset',
                          display: 'flex',
                          alignItems: 'center'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none !important'
                      },
                      '& .MuiSelect-icon': {
                          color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
                          right: 0
                      }
                  }
              }}
              />
            )}
          </Stack>
        </Stack>

        {/* Number */}
        <Stack flexGrow={1} justifyContent="center" width="100%">
          {renderNumberContent()}
        </Stack>

        {/* Change Percentage row — same for both themes */}
        {showChangePercentage && change !== undefined && (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" mt={1}>
            <Box display="flex" alignItems="center" color={getChangeColor()}>
              {change !== 0 && (
                changeType === 'increase'
                  ? <ArrowUpward fontSize="small" />
                  : <ArrowDownward fontSize="small" />
              )}
              <Typography ml={0.5} sx={{ color: getChangeColor() }}>
                {getPercentageText()}
              </Typography>
            </Box>

            {hasModal && (
              <Button
                variant="text"
                size="small"
                onClick={(e) => { e.stopPropagation(); onModalClick?.(); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isLightTheme ? colors.widget?.text?.action?.primary : t.modalButtonColor,
                  '&:hover': { backgroundColor: isLightTheme ? colors.widget?.background?.card : t.modalButtonHoverBg },
                }}
              >
                {modalText}
                <RedirectIcon style={{ width: 16, height: 16, marginLeft: 4 }} />
              </Button>
            )}
          </Box>
        )}
        {isCustomLabel && (
          <Typography ml={0.5} sx={{ color: colors.widget?.text?.noChange , fontWeight: '500'}} {...customLabelProps}>
                {customLabelText}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export const renderTooltipIcon = (tooltipLabel: any, theme: 'dark' | 'light' = 'light') => {
  const t = themeTokens[theme];
  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="body2" sx={{ color: t.tooltipTextColor, fontWeight: 500 }}>
            {tooltipLabel}
          </Typography>
        </Box>
      }
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            zIndex: 9999,
            ...(theme === 'light' && {
              backgroundColor: t.tooltipBg,
              border: t.tooltipBorder,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              color: t.tooltipTextColor,
            }),
            '& .MuiTooltip-arrow': {
              zIndex: 9999,
              ...(theme === 'light' && { color: t.tooltipArrowColor }),
            },
          },
        },
      }}
    >
      <IconButton
        sx={{
          position: 'relative',
          cursor: 'pointer',
          p: 0,
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {theme === 'light'
          ? <InfoOutlinedIcon sx={{ color: t.infoIconColor, fontSize: '22px' }} />
          : <InfoIcon sx={{ color: t.infoIconColor, fontSize: '16px' }} />
        }
      </IconButton>
    </Tooltip>
  );
};

export default KpiCard;